import { useState } from 'react';
import { toast } from 'react-toastify';
import { ServiceType } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Field,
  FormGrid,
  Input,
  LoadingBlock,
  Muted,
  PageContainer,
  PageHeader,
  Select,
  Stack,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../../components/ui';
import {
  useServiceTaxRulesQuery,
  useSetServiceTaxRuleMutation,
} from '../../services/serviceService/__generated__/queries.generated';
import { CheckRow } from '../ServiceProviderPage/serviceControls';
import { SERVICE_TYPES, SERVICE_TYPE_LABELS } from '../ServiceProviderPage/priceTables';
import { stayErrorMessage } from '../StayHostPage/stayErrors';

/**
 * Sales tax on service jobs (spec 019 D24), platform owner only: per state and
 * service, whether it is taxed, at what rate, and whether the platform fee is
 * taxed too. Tax is worked out where the customer's home or car is.
 */
export default function AdminServiceTaxPage() {
  const query = useServiceTaxRulesQuery({ fetchPolicy: 'network-only' });
  const [save, { loading }] = useSetServiceTaxRuleMutation({ refetchQueries: ['ServiceTaxRules'] });
  const [state, setState] = useState('');
  const [serviceType, setServiceType] = useState<ServiceType>(ServiceType.HomeTurnover);
  const [taxable, setTaxable] = useState(false);
  const [feeTaxable, setFeeTaxable] = useState(false);
  const [rate, setRate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const rules = [...(query.data?.serviceTaxRules ?? [])].sort((a, b) =>
    a.state.localeCompare(b.state),
  );

  const submit = async () => {
    const code = state.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(code)) {
      setError('Use the two-letter state code');
      return;
    }
    const value = rate.trim() ? Number(rate) : null;
    if (value !== null && !(value >= 0 && value <= 20)) {
      setError('The rate is a percent from 0 to 20');
      return;
    }
    if (taxable && value === null) {
      setError('A taxed service needs the state rate');
      return;
    }
    setError(null);
    try {
      await save({
        variables: {
          state: code,
          serviceType,
          taxable,
          platformFeeTaxable: feeTaxable,
          rate: value,
        },
      });
      toast.success('Tax rule saved');
    } catch (e) {
      setError(stayErrorMessage(e, 'Not saved'));
    }
  };

  const edit = (rule: (typeof rules)[number]) => {
    setState(rule.state);
    setServiceType(rule.serviceType);
    setTaxable(rule.taxable);
    setFeeTaxable(rule.platformFeeTaxable);
    setRate(rule.rate == null ? '' : String(rule.rate));
  };

  return (
    <PageContainer width="lg" canvas data-testid="admin-service-tax">
      <PageHeader
        eyebrow="Admin"
        title="Service tax"
        description="Off by default: a state and service with no rule is not taxed."
      />
      <Stack $gap={6}>
        <Card>
          <CardHeader title="Set a rule" />
          <CardBody>
            <Stack $gap={4}>
              <FormGrid $cols={3}>
                <Field label="State" htmlFor="tax-state-code">
                  <Input
                    id="tax-state-code"
                    maxLength={2}
                    placeholder="AZ"
                    value={state}
                    onChange={e => setState(e.target.value)}
                  />
                </Field>
                <Field label="Service" htmlFor="tax-service">
                  <Select
                    id="tax-service"
                    value={serviceType}
                    onChange={e => setServiceType(e.target.value as ServiceType)}
                  >
                    {SERVICE_TYPES.map(t => (
                      <option key={t} value={t}>
                        {SERVICE_TYPE_LABELS[t]}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Field label="Rate (%)" htmlFor="tax-rate" hint="Until ZIP rates arrive">
                  <Input
                    id="tax-rate"
                    inputMode="decimal"
                    value={rate}
                    onChange={e => setRate(e.target.value)}
                  />
                </Field>
              </FormGrid>
              <CheckRow
                label="This service is taxed in this state"
                checked={taxable}
                onChange={setTaxable}
              />
              <CheckRow
                label="Our service fee is taxed too"
                checked={feeTaxable}
                onChange={setFeeTaxable}
              />
              {error && (
                <Alert tone="error" title="Not saved">
                  {error}
                </Alert>
              )}
              <div>
                <Button
                  type="button"
                  text="Save rule"
                  onClick={submit}
                  isLoading={loading}
                  dataTestId="tax-rule-save"
                />
              </div>
            </Stack>
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Rules" />
          {query.loading && !query.data ? (
            <LoadingBlock label="Loading" />
          ) : query.error && !query.data ? (
            <CardBody>
              <Alert tone="error" title="Could not load">
                {stayErrorMessage(query.error)}
              </Alert>
            </CardBody>
          ) : rules.length === 0 ? (
            <CardBody>
              <Muted $size="sm">No rules yet. Nothing is taxed.</Muted>
            </CardBody>
          ) : (
            <TableWrap>
              <Table>
                <THead>
                  <TR>
                    <TH>State</TH>
                    <TH>Service</TH>
                    <TH>Taxed</TH>
                    <TH>Rate</TH>
                    <TH>Fee taxed</TH>
                    <TH />
                  </TR>
                </THead>
                <TBody>
                  {rules.map(rule => (
                    <TR key={`${rule.state}:${rule.serviceType}`}>
                      <TD>{rule.state}</TD>
                      <TD>{SERVICE_TYPE_LABELS[rule.serviceType]}</TD>
                      <TD>
                        <Badge tone={rule.taxable ? 'warning' : 'neutral'}>
                          {rule.taxable ? 'Yes' : 'No'}
                        </Badge>
                      </TD>
                      <TD>{rule.rate == null ? '—' : `${rule.rate}%`}</TD>
                      <TD>{rule.platformFeeTaxable ? 'Yes' : 'No'}</TD>
                      <TD>
                        <Button
                          type="button"
                          theme="text"
                          size="sm"
                          text="Edit"
                          onClick={() => edit(rule)}
                        />
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
          )}
        </Card>
      </Stack>
    </PageContainer>
  );
}
